/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { Alert, Link } from '@zapier/design-system';

import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import { BannerProps } from './types';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

const calculateAnnualSavingsAmount = (data: Result['data']) => {
  const { currentAccount } = data;
  const currentPlan = currentAccount.plan;
  const alternatePlan = data.possiblePlans
    .find((possiblePlan) => {
      return possiblePlan.planType === currentPlan.type;
    })
    ?.plans.find((plan) => {
      return plan.name === currentPlan.name && plan.interval === 'year';
    });
  const currentMonthlyAmount = currentPlan.amount || 0;
  const alternateMonthlyAmount =
    (alternatePlan?.amount || currentMonthlyAmount) / 12;
  const difference = currentMonthlyAmount - alternateMonthlyAmount;
  return difference / currentMonthlyAmount;
};

export const shouldRender = (data: Result['data']) => {
  const { currentAccount } = data;
  const currentPlan = currentAccount.plan;

  // If it's already an annual plan it can't be upsold, and users on legacy
  // plans shouldn't see the banner.
  if (currentPlan.interval === 'year' || !currentPlan.isInPlanGroup) {
    return false;
  }

  return calculateAnnualSavingsAmount(data) > 0;
};

export const Component = function AnnualBillingUpsell(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const { data } = props;
  const currentPlan = data.currentAccount.plan;
  const savingsAmount = calculateAnnualSavingsAmount(data);
  const savingsPercent = `${Math.floor(savingsAmount * 100)}%`;
  const upgradeUrl = encodeURI(
    `/app/billing/plans/change/${currentPlan.name.toLowerCase()}`
  );

  const onUpgrade = () => {
    props.trackEvent('clicked annual billing nudge');
  };

  const title = (
    <span>
      {`Save ${savingsPercent} when you `}
      <Link href={getFullyQualifiedURL(upgradeUrl)} onClick={onUpgrade}>
        switch to an annual subscription
      </Link>
    </span>
  );

  return <Alert layout={props.layout} title={title} type="info" />;
};
